/**
 * Document Analysis Modal Component
 * Shows cost estimate and gets user confirmation before processing.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { FileText, AlertCircle, Clock } from 'lucide-react';

const DOMAIN_TYPE_LABELS = {
  legal_agreement:   'Legal Agreement',
  medical_health:    'Medical / Health Report',
  financial:         'Financial Document',
  hr_employment:     'HR / Employment Record',
  identity_document: 'Identity Document',
  general:           'General Document',
};

export default function DocumentAnalysisModal({
  document,
  userCredits,
  onConfirm,
  onCancel,
  isOpen
}) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [domainType, setDomainType] = useState('general');

  // Sync domainType when analysis data arrives
  React.useEffect(() => {
    const detected = document?.analysis?.domainType;
    if (detected) setDomainType(detected);
  }, [document?.analysis?.domainType]);

  if (!document || !document.analysis) {
    return null;
  }

  const { analysis } = document;
  const {
    documentType,
    totalPages,
    estimatedCredits: rawEstimatedCredits,
    recommendation,
    confidence
  } = analysis;

  // Cross-method discount: if Categories already ran, subtract those credits
  const detections = document.detections || {};
  const categoriesCached = !!detections.categories;
  let estimatedCredits = rawEstimatedCredits;
  if (categoriesCached && estimatedCredits > 0) {
    const catCreditsUsed = detections.categories?.creditsUsed || 0;
    estimatedCredits = Math.max(1, estimatedCredits - catCreditsUsed);
  }

  const getProcessingTimeEstimate = (pages, method) => {
    const perPage = 15; // ~15 seconds per page for Claude Vision API
    const overhead = 10; // download, annotation, upload overhead
    const total = pages * perPage + overhead;
    return `~${total} seconds for ${pages} page${pages !== 1 ? 's' : ''}`;
  };

  const hasInsufficientCredits = userCredits < estimatedCredits;
  const remainingCredits = userCredits - estimatedCredits;

  const handleConfirm = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsProcessing(true);
    try {
      await onConfirm({ domainType });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isProcessing && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Document Analysis Complete</DialogTitle>
          <DialogDescription>
            Review the analysis results and confirm to begin processing
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Document Info */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <FileText className="w-5 h-5 text-gray-600" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {document.name}
              </p>
            </div>
          </div>

          {/* Analysis Results */}
          <div className="space-y-2">
            <h3 className="font-semibold text-sm text-gray-700">Analysis Results:</h3>
            <ul className="space-y-1 text-sm text-gray-600">
              <li>
                <span className="font-medium">Document Type:</span>{' '}
                {documentType === 'text' ? 'Text-based PDF' : 'Scanned/Image PDF'}
              </li>
              <li className="flex items-center gap-2">
                <span className="font-medium">POPIA Category:</span>
                <select
                  value={domainType}
                  onChange={e => setDomainType(e.target.value)}
                  className="text-sm border border-gray-300 rounded px-2 py-0.5
                             focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {Object.entries(DOMAIN_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </li>
              <li>
                <span className="font-medium">Total Pages:</span> {totalPages}
              </li>
              <li>
                <span className="font-medium">Confidence:</span>{' '}
                {(confidence * 100).toFixed(0)}%
              </li>
            </ul>
          </div>

          {/* Credit Estimate */}
          <div className={`p-4 rounded-lg border-2 ${
            hasInsufficientCredits ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'
          }`}>
            <h3 className="font-semibold text-sm text-gray-700 mb-2">
              Credit Estimate
            </h3>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Cost:</span>
                <span className="font-bold text-gray-900">
                  {estimatedCredits} credit{estimatedCredits !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>
                  ({recommendation === 'claude' ? '1' : '5'} credit per{' '}
                  {documentType === 'text' ? 'text' : 'image'} page)
                </span>
              </div>
              <div className="pt-2 mt-2 border-t border-gray-200">
                <div className="flex justify-between">
                  <span className="text-gray-600">Your Balance:</span>
                  <span className="font-medium text-gray-900">{userCredits} credits</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">After Processing:</span>
                  <span className={`font-medium ${
                    hasInsufficientCredits ? 'text-red-600' : 'text-emerald-600'
                  }`}>
                    {hasInsufficientCredits ? 'Insufficient' : `${remainingCredits} credits`}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Estimated Processing Time */}
          <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-medium text-blue-900">Estimated Processing Time</p>
              <p className="text-blue-700 mt-0.5">
                {getProcessingTimeEstimate(totalPages, recommendation)}
              </p>
            </div>
          </div>

          {/* Insufficient Credits Warning */}
          {hasInsufficientCredits && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="font-medium text-red-900">Insufficient Credits</p>
                <p className="text-red-700 mt-1">
                  You need {estimatedCredits - userCredits} more credits to process this document.
                </p>
                {/* TODO: Add link to credit purchase page when implemented */}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onCancel();
            }}
            disabled={isProcessing}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            onClick={(e) => handleConfirm(e)}
            disabled={hasInsufficientCredits || isProcessing}
            className="w-full sm:w-auto"
          >
            {isProcessing ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Processing...
              </>
            ) : (
              `Confirm & Process (${estimatedCredits} credits)`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
